const PAYMENT_SESSION_KEY = 'pendingPaymentOrder';

function asNonEmptyString(value) {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : undefined;
}

function collectObjects(...sources) {
  return sources.filter(
    (source) => Boolean(source) && typeof source === 'object' && !Array.isArray(source)
  );
}

function pickField(objects, keys) {
  for (const obj of objects) {
    for (const key of keys) {
      const value = asNonEmptyString(obj[key]);
      if (value) return value;
    }
  }
  return undefined;
}

function readRawSession() {
  try {
    const raw = localStorage.getItem(PAYMENT_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSession(session) {
  try {
    localStorage.setItem(PAYMENT_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

function mergeSession(partial) {
  const merged = { ...readRawSession(), ...partial };
  writeSession(merged);
  return merged;
}

export function persistPaymentSessionFromTempData(data) {
  const objects = collectObjects(data, data?.data);
  const next = {
    tempDataId: pickField(objects, ['id', 'temp_data_id', 'tempDataId']),
  };
  writeSession(next);
  return next;
}

function readSuccessFlag(obj) {
  if (obj.success === true || obj.success === 'true') return true;
  const status = asNonEmptyString(obj.status)?.toLowerCase();
  return status === 'success' || status === 'completed' || status === 'paid';
}

export function isPaymentSocketSuccess(data) {
  const objects = collectObjects(data, data?.data, data?.result);
  if (objects.some(readSuccessFlag)) return true;

  const orderId = pickField(objects, [
    'orderId',
    'order_id',
    'product_order_id',
    'productOrderId',
  ]);
  const orderNumber = pickField(objects, ['orderNumber', 'order_number']);
  if (orderId || orderNumber) return true;

  const eventName = asNonEmptyString(
    data?.event || data?.type || data?.name || data?.action
  )?.toLowerCase();
  return Boolean(eventName && /success|paid|complete/.test(eventName));
}

export function paymentEventMatchesTempId(data, tempDataId) {
  if (!tempDataId || !data || typeof data !== 'object') return false;
  const objects = collectObjects(data, data.data, data.result);
  const id = pickField(objects, [
    'temp_data_id',
    'tempDataId',
    'userId',
    'user_id',
    'id',
  ]);
  return id === String(tempDataId);
}

function collectOrderNumbers(...sources) {
  const seen = new Set();
  const numbers = [];
  const pushNumber = (value) => {
    const next = asNonEmptyString(value);
    if (!next || seen.has(next)) return;
    seen.add(next);
    numbers.push(next);
  };
  const visit = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value !== 'object') {
      pushNumber(value);
      return;
    }
    pushNumber(value.orderNumber);
    pushNumber(value.order_number);
    if (Array.isArray(value.orderNumbers)) value.orderNumbers.forEach(pushNumber);
    if (Array.isArray(value.order_numbers)) value.order_numbers.forEach(pushNumber);
    if (Array.isArray(value.orders)) value.orders.forEach(visit);
    if (value.order && typeof value.order === 'object') visit(value.order);
    if (value.data && typeof value.data === 'object') visit(value.data);
    if (value.result && typeof value.result === 'object') visit(value.result);
  };
  sources.forEach(visit);
  return numbers;
}

/** Persist order ids/numbers returned from POST /product/order/pay-on-reception. */
export function savePendingPaymentOrderFromApiOrders(orders, tempDataId) {
  const list = Array.isArray(orders) ? orders : [];
  const orderNumbers = collectOrderNumbers(list);
  const first = list[0] || {};
  const orderId =
    asNonEmptyString(first.id) ||
    asNonEmptyString(first.orderId) ||
    asNonEmptyString(first.order_id);

  return mergeSession({
    tempDataId,
    orderId,
    orderNumber: orderNumbers[0],
    orderNumbers: orderNumbers.length > 0 ? orderNumbers : undefined,
  });
}

export function savePendingPaymentOrderFromSocket(data, tempDataId) {
  const nested = collectObjects(data, data?.data, data?.order, data?.result);
  const orderObject = collectObjects(data?.order);
  const orderId =
    pickField(nested, ['orderId', 'order_id', 'product_order_id', 'productOrderId']) ||
    pickField(orderObject, ['orderId', 'id', 'order_id']);
  const orderNumbers = collectOrderNumbers(
    data,
    data?.data,
    data?.result,
    data?.orders,
    data?.order
  );
  const orderNumber =
    orderNumbers[0] ||
    pickField(nested, ['orderNumber', 'order_number']) ||
    pickField(orderObject, ['orderNumber', 'order_number', 'number']);
  const resolvedNumbers =
    orderNumbers.length > 0 ? orderNumbers : orderNumber ? [orderNumber] : [];

  return mergeSession({
    tempDataId: tempDataId || pickField(nested, ['temp_data_id', 'tempDataId']),
    orderId,
    orderNumber: orderNumber || resolvedNumbers[0],
    orderNumbers: resolvedNumbers.length > 0 ? resolvedNumbers : undefined,
  });
}

export function readPaymentSession() {
  return readRawSession();
}

export function readPendingOrderNumbers() {
  const session = readRawSession();
  if (Array.isArray(session.orderNumbers) && session.orderNumbers.length > 0) {
    return session.orderNumbers.map((value) => String(value).trim()).filter(Boolean);
  }
  const single = asNonEmptyString(session.orderNumber);
  return single ? [single] : [];
}

export function clearPendingPaymentOrder() {
  try {
    localStorage.removeItem(PAYMENT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}
